import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  ahuBoardFormsApi,
  type ICatalogRow,
} from 'app/services/ahuBoardFormsApi';
import { catalogLabel } from './formUtils';

/**
 * Phase 6e1 — PaginatedSelectView port (§7.7b, shared
 * /static/mvc/view/pageinated_select.js + pageinated_select.tmpl, markup
 * faithful): root div carries the legacy `custom-select-with-pagination`
 * class (+`open`) so common.css draws the caret + underline chrome, hidden
 * native `<select name class>` (so host-level change handlers & the verify
 * harness see the legacy control), custom `.selected` div + `.options` ul,
 * 50/page infinite scroll (<10 px from bottom → next page), `hasNoneOption`
 * "- -" dropdown row, closed-state '-----' for null values (legacy
 * selectedText/getValue), off-page `getValue` resolution via the list-url
 * `?id=` lookup, document click-outside close.
 *
 * [NEG]#46 fixed by construction: outside-click handlers are per-instance
 * React effects — removing one instance never unbinds the others.
 *
 * PERF (6e1 verify 6e1-3): off-page item resolution is memoized in a
 * module-level cache keyed by mfg|path|ccid|id, with in-flight dedupe and
 * `subscribe:false` dispatches — N selects resolving the same value share
 * ONE request and remounts resolve synchronously with zero dispatches.
 */

const RESOLUTION_CACHE = new Map<string, ICatalogRow | null>();
const RESOLUTION_INFLIGHT = new Map<string, Promise<ICatalogRow | null>>();

interface PaginatedSelectProps {
  mfgId: number;
  /** CMS path segment, e.g. 'ahu/casing/metal_sheets'. */
  path: string;
  constructionClassId?: number | null;
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  /** Hidden native select attrs (host handler contract). */
  selectName: string;
  selectClass?: string;
  /** "- -" option (everything except outerliner_id, §7.7b). */
  hasNoneOption?: boolean;
  /** Closed-state text when value is null — default '-----' (legacy
   * selectedText); the bulk panel passes 'Varies' for mixed values. */
  nullText?: string;
  /** Reports collection emptiness so the host can hide the row (§7.3.1). */
  onEmptyChange?: (empty: boolean) => void;
  /** Extra data-* stamped on the native select (int-wall material selects). */
  dataId?: number;
  dataPlane?: string;
  disabled?: boolean;
}

const PaginatedSelect = ({
  mfgId,
  path,
  constructionClassId,
  value,
  onChange,
  selectName,
  selectClass,
  hasNoneOption = true,
  nullText = '-----',
  onEmptyChange,
  dataId,
  dataPlane,
  disabled,
}: PaginatedSelectProps) => {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<ICatalogRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [resolved, setResolved] = useState<ICatalogRow | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);

  // Page loader (accumulating — legacy collection keeps all fetched pages).
  useEffect(() => {
    let cancelled = false;
    loadingRef.current = true;
    const sub = dispatch(
      ahuBoardFormsApi.endpoints.getCmsPaginated.initiate({
        mfgId,
        path,
        page,
        constructionClassId,
      })
    );
    sub
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        setCount(res.count);
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...res.results.filter((r) => !seen.has(r.id))];
        });
        // Legacy hide-empty: `collection.length < 1` after fetchPage(1) —
        // results length, not the count field (verify 6e2a-10 Floor Deck).
        if (page === 1) onEmptyChange?.(res.results.length < 1);
      })
      .catch(() => {
        // Fetch failure leaves the legacy collection empty → row hidden.
        if (!cancelled && page === 1) onEmptyChange?.(true);
      })
      .finally(() => {
        loadingRef.current = false;
      });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfgId, path, page, constructionClassId, dispatch]);

  // Reset when the catalog key changes (construction-class switch reloads).
  useEffect(() => {
    setRows([]);
    setPage(1);
    setResolved(null);
  }, [mfgId, path, constructionClassId]);

  // Off-page value resolution (getValue → collection.getById) — module-level
  // memo + in-flight dedupe (PERF note above).
  useEffect(() => {
    if (value == null) {
      setResolved(null);
      return undefined;
    }
    if (rows.some((r) => r.id === value)) return undefined;
    const key = `${mfgId}|${path}|${constructionClassId ?? ''}|${value}`;
    const cached = RESOLUTION_CACHE.get(key);
    if (cached !== undefined) {
      setResolved(cached);
      return undefined;
    }
    let cancelled = false;
    let promise = RESOLUTION_INFLIGHT.get(key);
    if (!promise) {
      promise = dispatch(
        ahuBoardFormsApi.endpoints.getCmsCatalogItem.initiate(
          { mfgId, path, id: value, constructionClassId },
          { subscribe: false }
        )
      )
        .unwrap()
        .then((row: ICatalogRow | null) => {
          RESOLUTION_CACHE.set(key, row ?? null);
          return row ?? null;
        })
        .catch(() => null)
        .finally(() => {
          RESOLUTION_INFLIGHT.delete(key);
        }) as Promise<ICatalogRow | null>;
      RESOLUTION_INFLIGHT.set(key, promise);
    }
    void promise.then((row) => {
      if (!cancelled) setResolved(row);
    });
    return () => {
      cancelled = true;
    };
  }, [value, rows, mfgId, path, constructionClassId, dispatch]);

  // Per-instance outside-click close ([NEG]#46 non-replication).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  const onScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 10 &&
      rows.length < count &&
      !loadingRef.current
    ) {
      setPage((p) => p + 1);
    }
  };

  const current = rows.find((r) => r.id === value) ?? resolved;
  // Legacy closed-state text: '-----' for null (initial selectedText AND the
  // getValue(null) branch) and '-----' until getValue resolves (R14). The
  // bulk panel overrides the null text with 'Varies' for mixed values.
  const selectedText = value == null ? nullText : catalogLabel(current) || '-----';

  const pick = (id: number | null) => {
    setOpen(false);
    onChange(id);
  };

  return (
    <div
      ref={rootRef}
      className={`custom-select-with-pagination${open ? ' open' : ''}${
        disabled ? ' non-clickable' : ''
      }`}
    >
      {/* Hidden native select — the legacy host-handler / harness contract. */}
      <select
        style={{ display: 'none' }}
        className={selectClass}
        name={selectName}
        value={value == null ? 'None' : String(value)}
        data-id={dataId}
        data-plane={dataPlane}
        onChange={(e) => {
          const v = e.target.value;
          pick(v === 'None' || v === '' ? null : parseInt(v, 10));
        }}
      >
        {hasNoneOption && <option value="None">- -</option>}
        {current && !rows.some((r) => r.id === current.id) && (
          <option value={current.id}>{catalogLabel(current)}</option>
        )}
        {rows.map((r) => (
          <option key={r.id} value={r.id}>
            {catalogLabel(r)}
          </option>
        ))}
      </select>
      <div
        className="selected"
        title={selectedText.length > 28 ? selectedText : undefined}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
      >
        {selectedText}
      </div>
      <ul
        className="options"
        style={{ display: open ? undefined : 'none' }}
        onScroll={onScroll}
      >
        {hasNoneOption && (
          <li data-id="" className="option-li" onClick={() => pick(null)}>
            {' '}
            - -{' '}
          </li>
        )}
        {rows.map((r) => (
          <li
            key={r.id}
            data-id={r.id}
            className={`option-li${r.id === value ? ' selected-li' : ''}`}
            onClick={() => pick(r.id)}
          >
            {catalogLabel(r)}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PaginatedSelect;
